import { LegalClientRepresentativeEntity } from 'api/types/entity';
import { TFunc } from 'libs/i18n';
import {
  isLegalRepresentativeCompany,
  parseActivityInitialValue,
  parseAddressValue,
  parseBiographyInitialValue,
  parseCitizenshipsValue,
  parseCompanyStateRegistrationValue,
  parsePepConnectionInitialValue,
  parseResidentialAddressValue,
  parseTaxResidencesValue,
} from 'modules/client/common/helpers';

import { LegalRepresentativeFormType } from '../../forms/legalRepresentativeForm';

import { parseIdentityDocumentInitialValue } from './parseIdentityDocumentInitialValue';
import { parseLegalClientRepresentativeDocumentsFiles } from './parseLegalClientRepresentativeDocumentsFiles';
import { parseRoleInCompanyValue } from './parseRoleInCompanyValue';

export const getLegalRepresentativeInitialValue = (
  t: TFunc,
  client: LegalClientRepresentativeEntity
): LegalRepresentativeFormType => {
  const isCompany = isLegalRepresentativeCompany(client);

  return {
    reasonChange: { reason: '' },
    contacts: {
      email: client.email,
      phone: client.phone,
    },
    personalData: {
      firstName: client.firstName,
      lastName: client.lastName,
      middleName: client.middleName ?? '',
      dateOfBirth: client.dateOfBirth,
      gender: client.gender
        ? {
            label: t(`client.client.personalData.gender.${client.gender}`),
            value: client.gender,
          }
        : undefined,
    },

    registrationAddress: parseAddressValue(client.registrationAddress),
    residentialAddress: parseResidentialAddressValue(client.residentialAddress),

    placeOfBirth: {
      country: client.placeOfBirth?.country ?? null,
      locality: client.placeOfBirth?.locality ?? '',
    },

    citizenships: parseCitizenshipsValue(client.citizenships),
    taxResidences: parseTaxResidencesValue(client.taxResidences),
    biography: parseBiographyInitialValue(t, client.biography),

    pepConnection: parsePepConnectionInitialValue(client.pepConnection, t),

    roleInCompany: parseRoleInCompanyValue(t, client.roleInCompany),

    companyName: isCompany
      ? {
          fullName: client.company?.fullName ?? '',
          shortName: client.company?.shortName,
        }
      : undefined,

    companyRegistrationAddress: isCompany
      ? parseAddressValue(client.company?.legalAddress)
      : undefined,
    companyResidentialAddress: isCompany
      ? parseAddressValue(client.company?.actualAddress)
      : undefined,

    companyTaxResidences: isCompany
      ? client.company?.taxResidences?.map((v) => ({
          country: v.country || null,
          taxNumber: v.taxId,
        })) || [{ country: null, taxNumber: '' }]
      : undefined,

    companyStateRegistration: isCompany
      ? parseCompanyStateRegistrationValue(client.company?.stateRegistration)
      : undefined,

    constituentDocsFiles: isCompany
      ? client.company?.constitutionalDocument?.files ?? []
      : [],

    constituentDocsDateOfLastChange: isCompany
      ? client.company?.constitutionalDocument?.dateOfLastChange
      : undefined,

    activity: client.user
      ? parseActivityInitialValue({ ...client, user: client.user })
      : {},

    identityDocument: parseIdentityDocumentInitialValue(t, client),
    additionalDocumentsFiles: client.additionalInformationFiles,
    documentsFiles: parseLegalClientRepresentativeDocumentsFiles(client),
  };
};
